<template>
	<InputRow
		:class="[
			!readOnly ? 'value-row-input' : 'value-row-calculated',
			type,
			type === 'checkbox' && isDisabled ? 'hidden' : '',
		]"
		:currency="valueType === ValueType.Currency"
		:percentage="valueType === ValueType.Percentage"
		:number="valueType === ValueType.Number"
		:for-id="uuid.toString()"
		:label="label"
	>
		<AppFieldNumeric
			v-if="isNumeric(valueType)"
			:id="uuid.toString()"
			v-model="modelValue as number | undefined"
			:type
			:required
			:disabled="isDisabled"
			:value-type
			:read-only="readOnly"
			:precision="
				precision !== undefined
					? precision
					: valueType === ValueType.Percentage
						? 2
						: 0
			"
			:grouping="valueType === ValueType.Currency"
			:prefix="valueType === ValueType.Currency ? '€' : ''"
			:suffix="valueType === ValueType.Percentage ? '%' : ''"
			wrapper-class="max-w-[200px]"
		/>
		<AppFieldDate
			v-else-if="valueType === ValueType.Date"
			:id="uuid.toString()"
			v-model="modelValue"
			:required
			:disabled="isDisabled"
			:value-type
			:read-only="readOnly"
			:show-display-date="false"
		/>
		<InputText
			v-else
			:id="uuid.toString()"
			v-model="modelValue"
			:type
			:required
			:disabled="isDisabled"
			:value-type
			:read-only="readOnly"
		/>
	</InputRow>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useUserStore } from "@/store/useUserStore";
import { ValueType } from "@/enums/ValueType";
import { isNumeric } from "@/helpers/valueTypeHelpers";

const props = withDefaults(
	defineProps<{
		required?: boolean;
		label: string;
		config?: any;
		readOnly?: boolean;
		uuid?: number;
		validation?: object;
		type?: string;
		validations?: object;
		precision?: number;
		placeholder?: string;
		valueType: ValueType;
	}>(),
	{
		required: false,
		config: () => ({ type: "text" }),
		readOnly: false,
		uuid: 0,
		validation: () => ({}),
		type: "text",
	},
);

const modelValue = defineModel<any>();

const userStore = useUserStore();
const isDisabled = computed(() => {
	return props.readOnly || userStore.user?.role !== "intermediary";
});
</script>

<style lang="scss">
.value-row.result {
	.input-wrapper,
	.app-field-numeric {
		border-radius: var(--border-radius);
	}

	&.neutral {
		.input-wrapper,
		.app-field-numeric {
			background: var(--hover-muted);
			color: var(--text-primary);
		}
	}

	&.success {
		.input-wrapper,
		.app-field-numeric {
			background: var(--color-success);
			color: var(--text-invert);
		}
	}

	&.failure {
		.input-wrapper,
		.app-field-numeric {
			background: var(--color-fail);
			color: var(--text-invert);
		}
	}
}

div.value-row {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	column-gap: 12px;
	margin-bottom: 6px;

	&.checkbox {
		.input-wrapper {
			width: auto;
			flex: unset;
		}
	}

	label {
		font-size: 0.85rem;
	}

	hr {
		flex-grow: 1;
		border: none;
		border-bottom: 1px dashed var(--border);
	}

	.input-wrapper {
		position: relative;
		flex: 0 0 200px;
	}

	@media screen and (max-width: var(--mobile-size)) {
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: 12px;
		gap: 2px;

		hr {
			display: none;
		}

		.input-wrapper,
		.dropdown-input {
			flex: unset;
			width: 100%;
		}
	}
}

.value-row-input {
	label {
		cursor: pointer;
	}

	.input-wrapper > input[type="checkbox"] {
		cursor: pointer;
		width: auto;
		margin-right: 8px;
	}
}

.input-wrapper > input,
.dropdown-input {
	padding: 0.25rem 0.5rem;
	color: inherit;
	font-family: inherit;

	&[disabled] {
		background-color: transparent;
	}
}
</style>
